import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { collection, collectionGroup, deleteDoc, doc, getDocs, limit, orderBy, query } from "firebase/firestore";
import { firebaseDb } from "../firebase/firebaseClient";
import { useResolvedMerchantId } from "../hooks/useResolvedMerchantId";

function timeAgo(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function PostsFeed({ merchantId }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const { merchantId: currentMerchantId } = useResolvedMerchantId(userInfo);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      setLoading(true);
      setError("");
      try {
        const ref = merchantId
          ? collection(firebaseDb, "merchants", merchantId, "posts")
          : collectionGroup(firebaseDb, "posts");
        const q = query(ref, orderBy("createdAt", "desc"), limit(20));
        const snapshot = await getDocs(q);
        if (!cancelled) {
          setPosts(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        }
      } catch (err) {
        if (!cancelled) setError("Could not load posts.");
        console.warn("[PostsFeed] fetchPosts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPosts();
    return () => { cancelled = true; };
  }, [merchantId]);

  const canDelete = (post) =>
    userInfo?.is_admin || (currentMerchantId && currentMerchantId === post.merchantId);

  const handleDelete = async (post) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    setDeletingId(post.id);
    try {
      await deleteDoc(doc(firebaseDb, "merchants", post.merchantId, "posts", post.id));
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      console.warn("[PostsFeed] handleDelete:", err);
      alert("Could not delete post. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <p className="text-muted small">Loading posts…</p>;
  }

  if (error) {
    return <p className="text-danger small">{error}</p>;
  }

  if (posts.length === 0) {
    return <p className="text-muted small">No posts yet. Be the first to share an update!</p>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {posts.map((post) => (
        <div key={post.id} className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-baseline mb-2">
              <span className="fw-semibold">{post.merchantName || "Unknown merchant"}</span>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">{timeAgo(post.createdAt)}</span>
                {canDelete(post) && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    style={{ fontSize: "0.7rem", padding: "1px 6px" }}
                    onClick={() => handleDelete(post)}
                    disabled={deletingId === post.id}
                  >
                    {deletingId === post.id ? "Deleting…" : "Delete"}
                  </button>
                )}
              </div>
            </div>

            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{post.text}</p>

            {post.photoUrl && (
              <img
                src={post.photoUrl}
                alt="Post"
                className="mt-3 rounded"
                style={{ width: "100%", maxHeight: "320px", objectFit: "cover" }}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default PostsFeed;
